import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/lib/utils";

// Multi-select filter for comma-separated API params (action=LOGIN,LOGOUT).
// `value` / `onChange` deal in the same comma-separated string the API expects.
//
// Moved here from `pages/super-admin/audit-logs/components/` when the tenant
// Compliance Audit Log page needed the same control: two areas render it now,
// and a page-local copy beside a second copy is the drift a shared kit exists
// to prevent.
const MultiSelectFilter = ({ label, options = [], value = "", onChange }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? value.split(",").filter(Boolean) : [];

  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt];
    onChange(next.join(","));
  };

  const allSelected = options.length > 0 && selected.length === options.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs border",
            selected.length > 0
              ? "bg-accent-soft/10 border-accent-border/30 text-accent-text"
              : "bg-background-form border-border-input text-text-primary"
          )}
        >
          <span className="truncate max-w-[120px]">
            {selected.length > 0 ? `${label} (${selected.length})` : label}
          </span>
          <ChevronDown size={12} className={cn("transition-transform", open && "rotate-180")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1 max-h-64 overflow-y-auto">
        {options.length === 0 ? (
          <div className="px-2 py-2 text-xs text-text-primary/50">No options</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-1 px-2 py-1 border-b border-border-primary/60 mb-1">
              <ButtonShadcn type="button" variant="ghost" size="xs" className="h-auto rounded-lg px-1.5 py-1 text-[11px] font-normal" disabled={allSelected} onClick={() => onChange(options.join(","))}>
                Select all
              </ButtonShadcn>
              <ButtonShadcn type="button" variant="ghost" size="xs" className="h-auto rounded-lg px-1.5 py-1 text-[11px] font-normal" disabled={selected.length === 0} onClick={() => onChange("")}>
                Clear all
              </ButtonShadcn>
            </div>

            {options.map((opt) => (
              <Button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className="w-full h-auto flex items-center gap-2 justify-start rounded-md px-2 py-1.5 text-xs font-normal text-text-primary hover:bg-background-icon/60"
              >
                <Checkbox checked={selected.includes(opt)} className="pointer-events-none" />
                <span className="truncate text-left">{opt}</span>
              </Button>
            ))}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectFilter;